/**
 * Layout presets — where the video and the branding/title overlays sit on the
 * fixed 1080x1920 canvas.
 *
 * `computeLayoutSlots` is the SINGLE source of truth for those coordinates. Both
 * the preview compositor (`apps/editor`) and the export renderer
 * (`apps/ecs/shorts-renderer-image`, which mirrors this file) call it, so a clip,
 * logo, or caption lands on the same pixel in preview and in the exported mp4.
 *
 * All rects are normalized (0..1) over the canvas, matching the `CropRect`
 * convention used elsewhere. Subtitle placement is expressed as a caption
 * anchor (`alignment` + `marginVPx`) so it flows through the existing caption
 * renderer unchanged.
 *
 * NOTE: the exact pixel values below are a sensible first pass derived from the
 * spec's deadzones and video sizes; they are expected to be fine-tuned in design
 * QA. Keep both engines reading this one function so a tweak stays in sync.
 */
import type { ClipFit, CropRect, LayoutPreset } from './edl';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from './edl';

/**
 * 쇼츠 앱이 **실제로 자기 UI 로 덮는** 자리, px @1080x1920.
 * 상단 검색·뒤로가기 180 / 우측 액션 버튼 120 / 하단 채널·설명·음악·탭바 350.
 * (근거: `docs/shorts-editor-quality-redesign.md` §"Shorts safe zone")
 *
 * 🔴 **에디터 프리뷰의 가이드가 읽는 값이 바로 이것이다.** 여기 있는 숫자와
 * 프리뷰가 그리는 띠가 어긋나면 프리뷰가 거짓말을 한다 — 예전에 그랬다.
 * `PreviewPane.tsx` 가 자기 사본(`180/350/120`, 왼쪽은 아예 없음)을 들고
 * 있으면서 주석에는 "`layout.ts` 와 같은 값"이라고 적어 뒀었다.
 * 값을 바꾸려면 여기만 바꾼다.
 *
 * `DEADZONE` 과 다른 이유: 저쪽은 **배치용 여유를 더한** 값이라 더 크다.
 */
export const SHORTS_UI = {
  top: 180,
  side: 120,
  bottom: 350,
} as const;

/**
 * Platform-UI safe area, in px on the 1080x1920 canvas (spec "Universal"
 * deadzones). UI/branding overlays should stay inside; the video itself may
 * bleed past horizontally since it is content, not chrome.
 *
 * {@link SHORTS_UI} 에 세로 여유를 더한 **배치 기준**이다(상 +70 / 하 +30).
 * 가로는 같다. 「가려지느냐」를 판정할 때는 이게 아니라 `SHORTS_UI` 를 쓴다.
 */
export const DEADZONE = {
  top: 250,
  side: 120,
  bottom: 380,
} as const;

/** Subtitle anchor, reusing the caption style's vertical placement fields. */
export interface SubtitleSlot {
  alignment: 'top' | 'center' | 'bottom';
  /** Vertical margin from the anchored edge, px @1080x1920. */
  marginVPx: number;
}

/**
 * 개입 자막(caption-2)이 발화 자막(caption-1)에서 떨어지는 거리, px @1080x1920.
 * 프리뷰(compositor)와 익스포트(render)가 **반드시 같은 값**을 써야 자막 위치가 일치한다.
 */
export const OVERLAY_CAPTION_OFFSET_PX = 150;

/**
 * CTA 글자 크기, px @1080x1920. **슬롯 높이와 무관한 고정값이다.**
 *
 * 예전에는 `슬롯높이 × 0.13` 으로 잡았는데, 그러면 배치를 옮기려고 상자를 줄일 때마다
 * 글자가 같이 작아진다. 실제로 하단 UI 를 피해 「기본」 프리셋 상자를 0.125→0.085 로
 * 줄였더니 글자가 31→21px 이 됐다(2026-09-11). 상자는 «어디에 놓을지»이고 글자
 * 크기는 «읽히느냐»다 — 둘은 따로 정해야 한다.
 *
 * 44px = 캔버스 높이의 2.3%. 6.1인치 폰에서 약 18pt 로, 3초 안에 읽힌다.
 * 프리뷰(compositor)와 익스포트(render)가 **같은 값**을 써야 한다.
 */
export const CTA_FONT_SIZE_PX = 44;

/**
 * 오버레이 노출 구간(초). 프리뷰(compositor)와 익스포트(render)가 **같은 값**을 써야 한다.
 *
 * 로고·채널명은 **상시 노출**이다 — 채널 브랜딩은 제품 정체성이라 도입부에만 띄우면
 * 시청 시간 대부분에 채널 표시가 없어진다. 반면 타이틀은 훅 역할이라 계속 두면 영상을
 * 가리므로 도입 3초, CTA는 마무리 3초로 제한한다.
 */
export const BRAND_WINDOW_SEC = Number.POSITIVE_INFINITY;
export const TITLE_WINDOW_SEC = 3;
export const CTA_WINDOW_SEC = 3;

/**
 * 개입 자막의 세로 여백. 'top' 앵커는 마진이 클수록 아래로 가므로 빼고,
 * 'bottom'이면 더해 한 줄 위로 올린다. 'center'는 그대로 둔다(겹칠 자리가 없다).
 */
export function overlayCaptionMarginV(slot: SubtitleSlot): number {
  if (slot.alignment === 'center') return slot.marginVPx;
  const delta = slot.alignment === 'top' ? -OVERLAY_CAPTION_OFFSET_PX : OVERLAY_CAPTION_OFFSET_PX;
  return Math.max(0, slot.marginVPx + delta);
}

export interface VideoSlot {
  /** Where the video is drawn on the canvas (0..1). */
  rect: CropRect;
  /** How a differently-shaped (e.g. 16:9) source fills that rect. */
  fit: ClipFit;
}

/**
 * 배경 위에 그리는 단색 띠. 영상·자막보다 **아래**에 깔린다.
 *
 * 왜 필요한가: `bands` 프리셋의 흰 자막띠는 «글자 배경» 이 아니라 **전폭 판**이다.
 * ASS 의 `BorderStyle: 3`(불투명 박스)은 글자를 감싸는 상자라 폭이 글자 길이에 따라
 * 변한다 — 자막이 바뀔 때마다 띠 길이가 춤춘다. 그래서 띠는 필터그래프가 그린다.
 */
export interface LayoutBand {
  /** 캔버스 정규화 좌표(0..1) — `CropRect` 관례와 같다. */
  rect: CropRect;
  /** #RRGGBB. */
  color: string;
}

/**
 * Resolved placement for every layout element. Absent-from-branding elements
 * (e.g. no logo configured) are simply not drawn — the slot is still returned.
 */
export interface LayoutSlots {
  /**
   * 배경 위·영상 아래에 그리는 단색 띠. 없거나 빈 배열이면 아무것도 안 그린다 —
   * 즉 기존 세 프리셋의 필터그래프는 **비트 단위로 그대로**다.
   */
  bands?: readonly LayoutBand[];
  video: VideoSlot;
  /** Platform logo (square-ish). */
  logo: CropRect;
  /** Channel name text box. */
  channelName: CropRect;
  /** Project title text box. */
  title: CropRect;
  /** Subtitle anchor (fed to the caption renderer). */
  subtitle: SubtitleSlot;
  /** CTA text block. */
  cta: CropRect;
}

// Normalized deadzone edges, precomputed.
const SIDE = DEADZONE.side / CANVAS_WIDTH; // ~0.111
const TOP = DEADZONE.top / CANVAS_HEIGHT; // ~0.130
const SAFE_W = 1 - 2 * SIDE; // ~0.778

/**
 * Compute the placement of every layout element for a preset.
 * Pure and deterministic — no time/random, safe to call every frame.
 */
/**
 * `bands` 프리셋의 측정된 밴드 좌표, px @1080x1920.
 *
 * 레퍼런스 채널 쇼츠 썸네일 **3장**을 픽셀로 재서 얻은 값이다(오차 ±3px). 추정이 아니라
 * 측정이고, 아래 파생값은 **이 값들에서 계산**한다 — 손으로 적으면 폰트를 바꿀 때 조용히
 * 어긋난다(오늘 자막이 화면을 넘친 사고의 원인이 바로 그 이중 정의였다).
 *
 * ⚠️ **블러 배경을 쓰지 않는다.** 처음엔 「원본을 9:16 에 꽉 채우고 강하게 블러」로 적어
 *    뒀는데, 밴드 좌표를 합쳐 보면 검정(0~494) + 흰띠(494~659) + 영상(659~1469) +
 *    검정(1469~1920) 이 **캔버스를 전부 덮는다** ⇒ 블러는 한 픽셀도 보이지 않는다.
 *    보이지 않는 것에 GPU 를 쓰지 않는다.
 */
export const BANDS = {
  /** 후킹 제목이 들어가는 검정 밴드. */
  title: { top: 218, height: 238 },
  /** 발화 자막이 들어가는 전폭 흰 띠. */
  caption: { top: 494, height: 165 },
  /** 영상. 1080x810 = 4:3 — 16:9 소스를 `cover` 로 넣으면 가로 0.75 가운데크롭이 된다. */
  video: { top: 659, height: 810 },
} as const;

/** 제목 글자 크기(기본). 넘치면 `fitTitleToBand` 가 줄인다. */
export const BANDS_TITLE_FONT_PX = 96;
/** 자막 글자 크기. 흰 띠 안에 두 줄이 들어가야 한다. */
export const BANDS_CAPTION_FONT_PX = 56;
/**
 * 줄 높이 배수. libass 가 폰트의 메트릭을 쓰므로 정확한 값은 폰트마다 다르다 —
 * 1.2 는 보수적인 근사이고, 이 값이 크면 자막이 띠 «안쪽» 으로 더 들어온다(안전한 방향).
 */
export const BANDS_LINE_HEIGHT_RATIO = 1.2;

/**
 * 밴드 자막이 가질 수 있는 최대 줄 수. `captions.ts` 의 `MAX_CAPTION_LINES` 와 같은 값이고
 * 루트 테스트가 두 자리를 값으로 대조한다(렌더러는 editor-core 를 import 할 수 없다).
 */
export const BANDS_CAPTION_MAX_LINES = 2;

/**
 * 자막 블록을 흰 띠 안에 세로 중앙정렬한 여백(px, 캔버스 위쪽 기준).
 *
 * 🔴 **줄 수를 인자로 받는다.** 옛 판은 늘 «두 줄» 로 계산해서, 한 줄 자막이 두 줄
 *    자리에 그려졌다. 프로덕션 컨테이너에서 실제로 렌더해 픽셀로 잰 값
 *    (2026-10-01, ffmpeg 5.1.9 + 컨테이너 폰트, 흰 띠 494~658 · 중심 576):
 *
 *      한 줄 → 글자 517~558 · 위여백 23 / **아래여백 100** ← 띠 중심보다 39px 위
 *      두 줄 → 글자 517~614 · 위여백 23 / 아래여백 44
 *
 *    그리고 그것은 예외가 아니라 **상시 모습**이었다. 프로덕션의 실제 whisper 단어
 *    타임코드로 쟀다 — 하이라이트 **40편**에서 30초로 기계 분할한 **296구간**에 실제
 *    분할기를 돌리면 자막 **3,824줄 중 1줄이 3,176개(83.1%)** · 2줄 630개(16.5%) 이고
 *    최장 한 줄이 정확히 10자(= `CAPTION_RULES.maxChars`)였다. 즉 자막 여섯 중 다섯이
 *    띠 위쪽에 붙어 있었다 — 「한 줄은 위에 붙는다」를 «드문 경우» 로 읽으면 안 되는
 *    자리였다.
 *    ⚠️ 표본은 **대리**다. 쇼츠의 전사는 vod-processor 가 본문으로 보내는데
 *       (`/internal/shorts-plan` 의 `req.words`) `SHORTS_ENABLED` 가 꺼져 있어 실물이
 *       아직 0편이다. 분할기와 whisper 모델이 같으므로 줄 수 분포는 옮겨 읽을 수 있다.
 *    ⚠️ 그리고 구간을 **기계로** 잘랐다 — 실제 후보는 LLM 이 고른다. 즉 이 분포는
 *       «분할기가 낼 수 있는 모습» 이고 실제 발생률의 **상한**이다.
 *
 * 🔴 {@link BANDS_CAPTION_MAX_LINES} 로 **상한을 건다 — 그리고 그 클램프는 실제로 닿는다.**
 *    위 표본에서 3줄 이상이 **18건(0.47%)** 이고 최대 **8줄**이다. 3줄 블록(201px)이 이미
 *    띠(165px)를 넘으므로, 그때는 옛 판과 같은 자리(510)에 그려 넘침이 **아래로만** 가게
 *    둔다. 위로 올리면 제목 밴드를 침범한다 — 영상 위로 흐르는 것보다 나쁘다.
 *    원인은 둘이고 **둘 다 게이트를 통과한다**:
 *      ① `CAPTION_RULES.maxChars = 10` 은 **한글 기준**이라 영어 전사가 잘게 쪼개진다
 *         (실측: `I'm going / to do it / for a new / year` 4줄). 환각이 아니라 정상 발화다.
 *      ② whisper 반복 환각 (`예에에에…` 8줄).
 *    `gateForSelection` 은 **윈도우 전체**의 고유어율을 보므로 구간 하나가 환각이어도
 *    막지 못한다 — 실측으로 40편 중 차단 **0편**이다.
 *    ⚠️ `MAX_CAPTION_LINES = 2` 가 이것을 막아 줄 거라고 읽지 마라. 그 상수가 비교되는
 *       자리는 `captions.ts` 의 **병합 경로 셋뿐**이고 뜻은 「병합해서 2줄을 넘으면
 *       병합하지 않는다」다. 처음부터 3줄인 자막은 아무도 막지 않는다.
 */
export function bandsCaptionMarginV(lines: number): number {
  const lineH = Math.round(BANDS_CAPTION_FONT_PX * BANDS_LINE_HEIGHT_RATIO);
  const n = Math.min(BANDS_CAPTION_MAX_LINES, Math.max(1, Math.floor(lines) || 1));
  return BANDS.caption.top + Math.round((BANDS.caption.height - lineH * n) / 2);
}

/**
 * 두 줄 자막의 여백. EDL 스타일의 기본값이고, 렌더러가 자막마다 줄 수로 덮어쓴다.
 * 이 상수를 그대로 쓰는 경로(`bands` 아닌 프리셋·에디터 프리뷰)는 동작이 바뀌지 않는다.
 */
export const BANDS_CAPTION_MARGIN_V = bandsCaptionMarginV(BANDS_CAPTION_MAX_LINES);

/**
 * 제목 노출 구간(초).
 *
 * 🔴 `bands` 는 **상시 노출**이다 — 제목이 «영상 위에 얹힌 정보» 가 아니라 **밴드의
 *    내용**이기 때문이다. 3초 뒤에 사라지면 검정 띠만 남는다. 다른 프리셋은 그대로 3초다
 *    (영상을 가리므로 도입부에만 필요하다 — `TITLE_WINDOW_SEC` 주석 참조).
 *
 * ⚠️ 렌더러는 `Math.min(창, 전체길이)` 로 클램프하므로 `Infinity` 가 곧 «끝까지» 다
 *    (`BRAND_WINDOW_SEC` 이 이미 그 방식이다).
 */
export function titleWindowSecFor(preset: LayoutPreset): number {
  return preset === 'bands' ? Number.POSITIVE_INFINITY : TITLE_WINDOW_SEC;
}

/**
 * CTA 노출 구간(초).
 *
 * 🔴 `bands` 는 **상시 노출**이다 — 하단 띠의 셋(로고·채널명·yoy.live)이 «한 세트» 라
 *    하나만 끝 3초에 나타나면 그 전까지는 두 개짜리 띠가 된다. 상수를 직접 쓰면 그렇게
 *    되고 오류는 안 난다(`titleWindowSecFor` 와 같은 이유·같은 모양이다).
 * ⚠️ 렌더러가 `Math.max(0, 전체길이 - 창)` 으로 쓰므로 `Infinity` 가 곧 «처음부터» 다.
 */
export function ctaWindowSecFor(preset: LayoutPreset): number {
  return preset === 'bands' ? Number.POSITIVE_INFINITY : CTA_WINDOW_SEC;
}

/**
 * CTA 의 ASS 앵커. 5 = 가운데 중앙, 6 = **오른쪽** 중앙.
 *
 * 🔴 `bands` 만 오른쪽이다. 가운데로 두면 로고·채널명이 왼쪽에 몰리고 오른쪽 60% 가
 *    비어 「레이아웃이 깨진 것 같다」로 읽힌다(2026-10-09 실물이 그랬다).
 *    6 이면 기준점이 상자의 **오른쪽 끝**이라 글자 길이가 바뀌어도 오른쪽이 고정된다 —
 *    가운데 앵커로는 ctaText 를 바꾸는 순간 위치가 따라 움직인다.
 */
export function ctaAnchorFor(preset: LayoutPreset): 5 | 6 {
  return preset === 'bands' ? 6 : 5;
}

/** CTA 글자 크기. `bands` 는 채널명(44px)보다 작게 둬서 «주인공» 을 헷갈리지 않게 한다. */
export function ctaFontPxFor(preset: LayoutPreset): number {
  return preset === 'bands' ? 36 : CTA_FONT_SIZE_PX;
}

/**
 * `bands` 프리셋의 제목 스타일. 검정 밴드 위 흰 굵은 글씨다.
 *
 * ⚠️ `fontSizePx` 는 **여기서 정하지 않는다** — 제목 길이에 따라 줄어들기 때문이다
 *    (`fitTitleToBand`). 호출부가 그 값으로 덮는다.
 * ⚠️ 폰트를 디스플레이 서체(예: Black Han Sans)로 바꾸는 것은 **육안 확인 사항**이다.
 *    렌더러 이미지는 그 서체를 내려받지만 실패해도 빌드가 통과하고(그때 libass 가 Noto
 *    CJK 로 조용히 대체한다) 패밀리 이름을 여기서 검증할 수단이 없다. 그래서 지금은
 *    이미 쓰고 있는 Pretendard 를 쓴다 — 확인되지 않은 이름을 넣지 않는다.
 */
export const BANDS_TITLE_STYLE = {
  fontFamily: 'Pretendard',
  primaryColor: '#FFFFFF',
  outlineColor: '#000000',
  outlineWidth: 10,
  bold: true,
} as const;

/**
 * `bands` 프리셋의 발화 자막 스타일. **흰 띠 위이므로 검정 글씨다.**
 *
 * 🔴 기본 발화 자막(`SPEECH_CAPTION_STYLE`)은 흰 글씨 + 검정 외곽선이다 — 영상 위에
 *    얹히는 전제다. 그걸 그대로 쓰면 **흰 띠 위에서 안 보인다.** 오류가 아니라 빈 띠가
 *    나오는 조용한 실패다.
 * ⚠️ 외곽선을 0 으로 둔다. 검정 글씨에 검정 외곽선은 글자를 굵게만 만들고, 흰 외곽선을
 *    주면 띠와 섞여 지저분해진다.
 * ⚠️ 자막이 두 줄을 넘으면 띠 밖(검정)으로 흘러 **검정 글씨가 검정 배경에** 놓인다.
 *    `MAX_CAPTION_LINES = 2` 가 그것을 막는 유일한 장치다 — 그 상한을 올리려면 이
 *    스타일과 띠 높이를 같이 다시 봐야 한다.
 */
export const BANDS_CAPTION_STYLE = {
  fontFamily: 'Pretendard',
  fontSizePx: BANDS_CAPTION_FONT_PX,
  primaryColor: '#000000',
  outlineColor: '#000000',
  outlineWidth: 0,
  bold: true,
} as const;

export function computeLayoutSlots(preset: LayoutPreset): LayoutSlots {
  if (preset === 'bands') {
    /*
     * 자동 생성 전용. 배경은 렌더러가 이미 깔는 검정 캔버스이므로 «검정 밴드» 를 따로
     * 그릴 필요가 없다 — 덮을 것이 없다. 그래서 띠는 흰 것 하나다.
     *
     * 브랜딩(로고·채널명·CTA)은 이 레이아웃에 **자리가 거의 없다.** 아래 검정 여백
     * 1469~1920 중에서 쇼츠 앱 UI(하단 350px = 1570~)를 피하면 101px 뿐이다. 자동
     * 파이프라인은 브랜딩 대신 **아웃트로**를 붙이므로 이 슬롯들은 쓰이지 않지만,
     * 사람이 이 프리셋을 쓰게 되는 날 겹치지 않도록 그 101px 안에 세로로 쌓아 둔다.
     */
    return {
      bands: [
        {
          rect: {
            x: 0,
            y: BANDS.caption.top / CANVAS_HEIGHT,
            w: 1,
            h: BANDS.caption.height / CANVAS_HEIGHT,
          },
          color: '#FFFFFF',
        },
      ],
      title: {
        x: 0,
        y: BANDS.title.top / CANVAS_HEIGHT,
        w: 1,
        h: BANDS.title.height / CANVAS_HEIGHT,
      },
      video: {
        rect: {
          x: 0,
          y: BANDS.video.top / CANVAS_HEIGHT,
          w: 1,
          h: BANDS.video.height / CANVAS_HEIGHT,
        },
        // 16:9 → 4:3 상자에 cover = 스케일 0.75 후 가로 가운데크롭. 측정된 w=0.75 와 같다.
        fit: 'cover',
      },
      subtitle: { alignment: 'top', marginVPx: BANDS_CAPTION_MARGIN_V },
      /*
       * 🔴 A안 (2026-10-09 승인) — 세 요소를 **한 줄에 양끝 정렬**한다.
       *
       *      [플랫폼로고] 채널명 ····························· yoy.live
       *
       *    쓸 수 있는 자리는 영상 아래 1469 부터 앱 UI 가 시작하는 1570 까지 **101px**
       *    뿐이다(위 머리말 참조). 셋의 **세로 중심을 1519 로 맞춘다** — 박스 높이는
       *    글자 크기를 정하는 값이기도 해서 서로 다르다.
       *
       *    ⚠️ 옛 값은 로고 90x50 @1475 · 채널명 @1475 · CTA 가운데 @1530 이었고,
       *       실물에서 셋 다 왼쪽에 몰려 오른쪽 60% 가 비었다. 게다가 CTA(44px, 중심
       *       1549)는 **1571 까지 내려가 UI 영역을 1px 넘었다** — 앱이 가리는 자리다.
       */
      logo: {
        x: 40 / CANVAS_WIDTH,
        y: 1487 / CANVAS_HEIGHT,
        w: 64 / CANVAS_WIDTH,
        h: 64 / CANVAS_HEIGHT,
      },
      channelName: {
        x: 124 / CANVAS_WIDTH,
        y: 1477 / CANVAS_HEIGHT,
        w: 620 / CANVAS_WIDTH,
        // 🔴 글자 크기는 `h * 0.52` 로 유도된다(render.ts) — 85 ⇒ 44px.
        h: 85 / CANVAS_HEIGHT,
      },
      cta: {
        // 앵커가 **오른쪽 끝**(`ctaAnchorFor`)이라 x+w 가 기준이다: 744+296 = 1040 (여백 40).
        x: 744 / CANVAS_WIDTH,
        y: 1485 / CANVAS_HEIGHT,
        w: 296 / CANVAS_WIDTH,
        h: 69 / CANVAS_HEIGHT,
      },
    };
  }
  if (preset === 'full') {
    // 영상이 캔버스 전체를 채운다 (쇼츠 기본형). 오버레이는 플랫폼 UI 데드존을 피해
    // 배치한다 — Shorts는 상단 ~180px(검색/네비), 하단 ~350px(채널·제목·음악 바),
    // 우측 ~120px(액션 버튼)을 자체 UI가 덮는다.
    return {
      video: { rect: { x: 0, y: 0, w: 1, h: 1 }, fit: 'cover' },
      logo: { x: SIDE, y: 0.105, w: 0.083, h: 0.047 },
      channelName: { x: SIDE + 0.105, y: 0.105, w: SAFE_W - 0.105 - 0.11, h: 0.047 },
      title: { x: SIDE, y: 0.165, w: SAFE_W - 0.11, h: 0.075 },
      // 하단 데드존(350px) 위에 자막을 올린다: 1920-350 = 1570 → 여유를 둬 400px.
      subtitle: { alignment: 'bottom', marginVPx: 400 },
      /*
       * CTA 는 자막 **위**에 둔다. 아래로 내리면 갈 곳이 없다 — 하단 350px 은
       * 앱 UI 가 덮고(0.818~), 그 위는 자막 차지다.
       *   개입 자막(caption-2) 하단 = (1920-550)/1920 = 0.713, 글자 높이까지 0.66.
       *   → CTA 는 0.65 에서 끝나야 안 겹친다.
       * 예전 값 0.875 는 하단 UI 안에 통째로 들어가 **항상 가려졌다**.
       */
      cta: { x: 0.5 - 0.12, y: 0.56, w: 0.24, h: 0.09 },
    };
  }
  if (preset === 'expanded') {
    // Video enlarged to fill the width and cropped (spec "확대", 16:9 → cover).
    // Title on top; subtitle overlaps the video's lower third; logo + channel
    // name sit below the video; CTA at the very bottom.
    return {
      title: { x: SIDE, y: 0.145, w: SAFE_W, h: 0.085 },
      video: { rect: { x: 0, y: 0.25, w: 1, h: 0.5 }, fit: 'cover' },
      subtitle: { alignment: 'bottom', marginVPx: 560 },
      // 0.775 는 하단이 0.822 라 앱 UI(0.818~) 에 **모서리가 물렸다**. 한 줄 올린다.
      logo: { x: SIDE, y: 0.762, w: 0.083, h: 0.047 },
      channelName: { x: SIDE + 0.105, y: 0.762, w: SAFE_W - 0.105, h: 0.047 },
      /*
       * 이 프리셋은 아래가 꽉 찼다 — 영상이 0.75 까지 오고 그 밑은 로고·채널명,
       * 그 밑은 앱 UI 다. 그래서 CTA 만 영상 위로 올려 자막과 겹치지 않는
       * 가운데(개입 자막 상단 0.586 위)에 둔다.
       */
      cta: { x: 0.5 - 0.12, y: 0.455, w: 0.24, h: 0.125 },
    };
  }
  // 'basic': logo + channel name on top, then title, then a centered 16:9
  // letterboxed video (spec 1080x608 ≈ h 0.317), subtitle just below it, CTA at
  // the bottom.
  return {
    logo: { x: SIDE, y: TOP + 0.015, w: 0.089, h: 0.05 },
    channelName: { x: SIDE + 0.11, y: TOP + 0.015, w: SAFE_W - 0.11, h: 0.05 },
    title: { x: SIDE, y: 0.245, w: SAFE_W, h: 0.09 },
    video: { rect: { x: 0, y: 0.342, w: 1, h: 0.317 }, fit: 'contain' },
    subtitle: { alignment: 'top', marginVPx: 1300 },
    /*
     * 자막 아래(0.721~) 와 앱 UI(0.818~) 사이에 끼워 넣는다. 0.097 밖에 없어서
     * 높이를 0.125 → 0.085 로 줄였다. 이 상자는 **놓을 자리일 뿐** 글자 크기와는
     * 무관하다 — 크기는 `CTA_FONT_SIZE_PX` 가 따로 정한다.
     */
    cta: { x: 0.5 - 0.12, y: 0.728, w: 0.24, h: 0.085 },
  };
}
